import type { Settings } from '../settings/schema.ts';
import { DEFAULT_SETTINGS } from '../settings/schema.ts';
import type { CustomStylePreset } from './customStylePresets.ts';
import { getCustomStylePreset, normalizeCustomStylePresets } from './customStylePresets.ts';
import {
  applyTrackStyleToSettings,
  trackStyleFromSettings,
  type TrackStyle,
} from './stylePresets.ts';
import { toStyleLanguageMatchKey } from './styleLanguageMatch.ts';
import {
  normalizeStyleLanguagePriorities,
  type StyleLanguagePriorities,
} from './styleLanguagePriorities.ts';

const STYLE_COMPARE_KEYS: (keyof TrackStyle)[] = [
  'fontSizeRatio',
  'fontWeight',
  'fontColor',
  'strokeWidth',
  'strokeColor',
  'shadow',
  'shadowColor',
  'fontBackgroundColor',
  'fontBackgroundColorOpacity',
  'fontFamily',
  'fontGoogleFamilies',
  'fontGoogleCssProperties',
  'letterSpacing',
  'lineHeight',
  'padding',
  'opacity',
  'boxShadow',
];

function stylesEqual(a: TrackStyle, b: TrackStyle): boolean {
  return STYLE_COMPARE_KEYS.every((key) => a[key] === b[key]);
}

export function resolvePresetForLanguage(
  matchKey: string,
  priorities: StyleLanguagePriorities,
  presets: readonly CustomStylePreset[],
): CustomStylePreset | undefined {
  if (!matchKey) return undefined;
  const presetId = priorities[matchKey];
  if (!presetId) return undefined;
  return getCustomStylePreset(presets, presetId);
}

/**
 * Build a runtime style patch for one slot when language has a priority style.
 * Does not modify customStylePresets or the caller's saved settings object —
 * callers should apply the patch only to page-runtime display state.
 */
export function buildAutoApplyStylePatch(
  languageCode: string | null | undefined,
  slot: 'main' | 'second',
  presetsRaw: unknown,
  prioritiesRaw: unknown,
  settings: Partial<Settings>,
): Partial<Settings> | null {
  const matchKey = toStyleLanguageMatchKey(languageCode || '');
  if (!matchKey) return null;
  const presets = normalizeCustomStylePresets(presetsRaw);
  const priorities = normalizeStyleLanguagePriorities(prioritiesRaw, presets);
  const preset = resolvePresetForLanguage(matchKey, priorities, presets);
  if (!preset) return null;

  const merged = { ...DEFAULT_SETTINGS, ...settings };
  const current = trackStyleFromSettings(merged, slot);
  if (stylesEqual(current, preset.style)) return null;
  return applyTrackStyleToSettings(preset.style, slot);
}

/** Storage keys that auto-apply may write (whitelist for content-script). */
export const AUTO_APPLY_STYLE_STORAGE_KEYS = [
  'mainFontSizeRatio',
  'mainFontWeight',
  'mainFontColor',
  'mainStrokeWidth',
  'mainStrokeColor',
  'mainShadow',
  'mainShadowColor',
  'mainFontBackgroundColor',
  'mainFontBackgroundColorOpacity',
  'mainFontFamily',
  'mainFontGoogleFamilies',
  'mainFontGoogleCssProperties',
  'mainLetterSpacing',
  'mainLineHeight',
  'mainPadding',
  'mainOpacity',
  'mainBoxShadow',
  'secondFontSizeRatio',
  'secondFontWeight',
  'secondFontColor',
  'secondStrokeWidth',
  'secondStrokeColor',
  'secondShadow',
  'secondShadowColor',
  'secondFontBackgroundColor',
  'secondFontBackgroundColorOpacity',
  'secondFontFamily',
  'secondFontGoogleFamilies',
  'secondFontGoogleCssProperties',
  'secondLetterSpacing',
  'secondLineHeight',
  'secondPadding',
  'secondOpacity',
  'secondBoxShadow',
] as const;
